import { Container, ContentLayout, Header, Tabs, TabsProps } from "@amzn/awsui-components-console";
import React from "react";
import { useLocation } from "react-router-dom";

import { useFeatureFlag } from "src/api/featureFlags";
import ConfigDrivenClaimsHistory from "src/claimsHistory/ConfigDrivenClaimsHistory";
import { routeLineOfBusiness, SEARCH_CONFIG_REGISTRY } from "src/claimsHistory/registry";
import CargoClaimsHistory from "src/components/cargoClaimsHistory/CargoClaimsHistory";
import IPLClaimsHistory from "src/components/iplClaimsHistory/IPLClaimsHistory";
import LoadingIndicator from "src/components/loadingIndicator/LoadingIndicator";
import WorkersCompensationClaimsHistory from "src/components/workersCompensationClaimsHistory/WorkersCompensationClaimsHistory";
import type { LobKey } from "src/jsonForms/preScreen/types";

const SEARCH_TAB_LABEL = "Search for Claims";

/**
 * The per-line-of-business components, selected by the line of business the
 * route resolves to. Workers Compensation is the fallback because it is what an
 * unresolved route rendered before the configuration-driven page existed.
 */
function legacyPresentation(lob: LobKey | null, mySubmittedClaims: boolean): React.ReactElement {
  if (mySubmittedClaims) {
    return (
      <WorkersCompensationClaimsHistory
        showUserClaimsOnly={true}
        testId="wc-claims-search-results"
      />
    );
  }

  switch (lob) {
    case "cargo":
      return <CargoClaimsHistory showUserClaimsOnly={false} testId="cargo-claims-search-results" />;
    case "ipl":
      return <IPLClaimsHistory showUserClaimsOnly={false} testId="ipl-claims-search-results" />;
    default:
      return (
        <WorkersCompensationClaimsHistory
          showUserClaimsOnly={false}
          testId="wc-claims-search-results"
        />
      );
  }
}

/**
 * The claims history page shell and the boundary between the two presentations.
 *
 * The shell — the heading, the container, and the tabs — is common to both, so
 * it lives here and neither presentation renders tabs of its own. Everything
 * below a tab belongs to exactly one presentation: the flag decides which, and
 * the other is never mounted, which is what makes the flag a rollback rather
 * than a partial migration.
 */
const ClaimsHistory = () => {
  const { pathname } = useLocation();
  const { value: configDriven, isLoading } = useFeatureFlag("enableConfigDrivenClaimsHistory");

  // The route is the one source of the line of business. Reading it here as the
  // configuration resolver reads it leaves a single derivation, rather than this
  // view trusting a value another page derived from the same pathname earlier.
  const lob = routeLineOfBusiness(pathname);

  /**
   * The my-submitted-claims declaration for this line of business, which is
   * both whether to offer the tab and what to label it. Read from the declared
   * configuration rather than from a resolved one because the tab set belongs
   * to the shell, which renders before any configuration is retrieved and on
   * both sides of the flag.
   */
  const mySubmittedClaimsMode =
    lob === null ? undefined : SEARCH_CONFIG_REGISTRY[lob]?.searchConfig.mySubmittedClaims;

  const presentationFor = (mySubmittedClaims: boolean): React.ReactNode => {
    if (isLoading) {
      return (
        <LoadingIndicator label="Loading claims history" testId="claims-history-flag-loading" />
      );
    }
    // A resolved false and a failed lookup arrive here identically, and both
    // want the same answer: the legacy page works, so an unavailable flag falls
    // back to it rather than to an error.
    return configDriven ? (
      <ConfigDrivenClaimsHistory mySubmittedClaims={mySubmittedClaims} />
    ) : (
      legacyPresentation(lob, mySubmittedClaims)
    );
  };

  const tabs: TabsProps.Tab[] = [
    {
      label: SEARCH_TAB_LABEL,
      id: "searchResults",
      content: presentationFor(false),
    },
  ];

  if (mySubmittedClaimsMode !== undefined) {
    tabs.push({
      label: mySubmittedClaimsMode.tabLabel,
      id: "mySubmittedClaims",
      content: presentationFor(true),
    });
  }

  return (
    <ContentLayout disableOverlap={false} header={<Header variant="h1">Claims History</Header>}>
      <Container fitHeight>
        <Tabs fitHeight data-testid="claims-history-tabs" tabs={tabs} />
      </Container>
    </ContentLayout>
  );
};

export default ClaimsHistory;
